/**
 * WatchForm — create / full-document edit form for one flight-tracker watch
 * (CHO-769). Prop-driven: no fetching, no watch validation — every rule lives
 * in the tracker CLI, whose stderr the page relays into `error` verbatim.
 *
 * Date ranges are paired <input type="date"> fields (From/To), not the numeric
 * DualRangeSlider. `name` is immutable in the tracker: editable on create,
 * read-only on edit (rename = create new + disable old). `stopovers` is a raw
 * JSON textarea for v1 — the backend validates the shape anyway.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import type { TrackerWatch, WatchDocument } from '../../types/flights';
import { DISABLE_EXPLAINER } from './WatchListTable';

export interface WatchFormProps {
  /** null → create mode (name editable). A watch → edit mode (name read-only,
   *  enabled checkbox, current values prefilled). */
  initial: TrackerWatch | null;
  submitting: boolean;
  /** Server-relayed tracker CLI stderr, displayed verbatim. */
  error: string | null;
  onSubmit: (doc: WatchDocument) => void;
  onCancel: () => void;
  /** Edit-mode enabled switch as a CONTROLLED value. The page lifts the state
   *  so a table toggle of the same watch while the editor is open is
   *  reflected in the form (saving must not undo it with a stale snapshot).
   *  When omitted the form owns the state (gallery, isolated use). */
  enabled?: boolean;
  onEnabledChange?: (enabled: boolean) => void;
  /** Extra submit-disable, e.g. a table toggle of the edited watch is in
   *  flight and a save right now would PUT a stale `enabled`. */
  saveDisabled?: boolean;
  /** Called whenever the form's local pre-submit error appears or clears.
   *  A parse failure returns before onSubmit — the only path that normally
   *  clears the server-relayed `error` — so the parent learns about the
   *  blocking parse error here and drops its now-obsolete copy. */
  onFormErrorChange?: (message: string | null) => void;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
      {children}
    </label>
  );
}

const inputClass =
  'w-full rounded border border-gray-300 px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-gray-400';

/** "BKK, SIN" ⇄ ['BKK', 'SIN'] */
function listToText(list: string[]): string {
  return list.join(', ');
}

function textToList(text: string): string[] {
  return text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

export function WatchForm({
  initial,
  submitting,
  error,
  onSubmit,
  onCancel,
  enabled: enabledProp,
  onEnabledChange,
  saveDisabled = false,
  onFormErrorChange,
}: WatchFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [origin, setOrigin] = useState(initial?.origin ?? '');
  const [destinations, setDestinations] = useState(listToText(initial?.destinations ?? []));
  const [outboundFrom, setOutboundFrom] = useState(initial?.outbound.from ?? '');
  const [outboundTo, setOutboundTo] = useState(initial?.outbound.to ?? '');
  const [inboundFrom, setInboundFrom] = useState(initial?.inbound.from ?? '');
  const [inboundTo, setInboundTo] = useState(initial?.inbound.to ?? '');
  const [tripTypes, setTripTypes] = useState(listToText(initial?.trip_types ?? ['return']));
  const [carriers, setCarriers] = useState(listToText(initial?.carriers ?? []));
  // Defaults apply at INITIALIZATION only. Clearing a field afterwards must
  // submit the empty/invalid value as-is, so the tracker CLI's validation is
  // the single source of truth instead of silent client-side fallbacks.
  const [cabin, setCabin] = useState(initial?.cabin ?? 'economy');
  const [passengers, setPassengers] = useState(String(initial?.passengers ?? 1));
  const [currency, setCurrency] = useState(initial?.currency ?? 'CHF');
  const [stopoversText, setStopoversText] = useState(
    initial && initial.stopovers.length > 0 ? JSON.stringify(initial.stopovers, null, 2) : '',
  );
  const [enabledInternal, setEnabledInternal] = useState(initial?.enabled ?? true);
  const enabled = enabledProp ?? enabledInternal;
  const setEnabled = onEnabledChange ?? setEnabledInternal;
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    // The document itself must be encodable to reach the CLI — this parse is
    // form hygiene (like type="date"), not watch validation.
    let stopovers: WatchDocument['stopovers'] = [];
    if (stopoversText.trim()) {
      try {
        stopovers = JSON.parse(stopoversText);
      } catch (err) {
        // The parse error must win the alert region over an earlier server
        // error: it is why THIS submission did not run. onSubmit never fires
        // on this path, so the parent only learns of the blocking error via
        // the callback and clears its obsolete relayed copy.
        const message = `Stopovers must be valid JSON: ${(err as Error).message}`;
        setFormError(message);
        onFormErrorChange?.(message);
        return;
      }
    }
    setFormError(null);
    onFormErrorChange?.(null);
    const doc: WatchDocument = {
      // Edit mode: the name is immutable and shown read-only, so submit it
      // EXACTLY as stored — the tracker accepts names with surrounding
      // whitespace (' winter '), and trimming here would trip the route's
      // body/path name-match 400. Create mode trims operator-typed padding.
      name: initial ? initial.name : name.trim(),
      origin: origin.trim(),
      destinations: textToList(destinations),
      outbound: { from: outboundFrom, to: outboundTo },
      inbound: { from: inboundFrom, to: inboundTo },
      trip_types: textToList(tripTypes),
      carriers: textToList(carriers),
      // No `|| 'economy'` / `|| 1` / `|| 'CHF'` here: a cleared field must
      // reach the CLI unchanged (empty string / NaN → JSON null) so the
      // tracker's own validation produces the error the route relays.
      cabin: cabin.trim(),
      // Whole-value numeric conversion: parseInt('1e2') silently truncates to
      // 1, but the number input accepts the same text as 100 — the tracker
      // would persist the wrong count. Empty input stays NaN (→ JSON null on
      // the wire) so a cleared field still reaches the CLI for its own
      // rejection instead of becoming a silent 0.
      passengers: passengers.trim() === '' ? NaN : Number(passengers),
      currency: currency.trim(),
      stopovers,
      // Edit must always carry enabled: the CLI preserves current state when a
      // document omits it, which would silently un-disable nothing but also
      // never re-enable — carrying it makes the checkbox authoritative.
      ...(initial ? { enabled } : {}),
    };
    onSubmit(doc);
  };

  // The local parse error wins: it is why the CURRENT submit did not run,
  // while `error` is the previous attempt's relayed CLI stderr — showing that
  // again would hide the error actually blocking submission (review round 6).
  const shownError = formError ?? error;

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Field label="Name">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            readOnly={initial != null}
            required
            placeholder="zrh-bkk-autumn"
            className={`${inputClass} ${initial != null ? 'bg-gray-100 text-gray-500' : ''}`}
            title={initial != null ? 'Watch names are immutable — create a new watch and disable this one to rename.' : undefined}
          />
        </Field>
        <Field label="Origin (IATA)">
          <input type="text" value={origin} onChange={(e) => setOrigin(e.target.value)} required placeholder="ZRH" className={inputClass} />
        </Field>
        <Field label="Destinations (comma-separated IATA)">
          <input type="text" value={destinations} onChange={(e) => setDestinations(e.target.value)} required placeholder="BKK, SIN" className={inputClass} />
        </Field>
        <Field label="Outbound — from">
          <input type="date" value={outboundFrom} onChange={(e) => setOutboundFrom(e.target.value)} required className={inputClass} />
        </Field>
        <Field label="Outbound — to">
          <input type="date" value={outboundTo} onChange={(e) => setOutboundTo(e.target.value)} required className={inputClass} />
        </Field>
        <Field label="Inbound — from">
          <input type="date" value={inboundFrom} onChange={(e) => setInboundFrom(e.target.value)} required className={inputClass} />
        </Field>
        <Field label="Inbound — to">
          <input type="date" value={inboundTo} onChange={(e) => setInboundTo(e.target.value)} required className={inputClass} />
        </Field>
        <Field label="Trip types (comma-separated)">
          <input type="text" value={tripTypes} onChange={(e) => setTripTypes(e.target.value)} placeholder="return, oneway" className={inputClass} />
        </Field>
        <Field label="Carriers (comma-separated IATA)">
          <input type="text" value={carriers} onChange={(e) => setCarriers(e.target.value)} placeholder="EY, EK" className={inputClass} />
        </Field>
        <Field label="Cabin">
          <input type="text" value={cabin} onChange={(e) => setCabin(e.target.value)} placeholder="economy" className={inputClass} />
        </Field>
        <Field label="Passengers">
          <input type="number" min={1} value={passengers} onChange={(e) => setPassengers(e.target.value)} className={inputClass} />
        </Field>
        <Field label="Currency">
          <input type="text" value={currency} onChange={(e) => setCurrency(e.target.value)} placeholder="CHF" className={inputClass} />
        </Field>
        {initial != null && (
          <Field label="State">
            <label className="flex items-center gap-2 text-sm text-gray-700 h-[34px]">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="rounded" />
              Enabled (query this watch)
            </label>
          </Field>
        )}
        {initial != null && !enabled && (
          // Same copy as the table's disable confirm: unchecking Enabled here
          // is the same end-of-life action, so the history-kept / re-enable /
          // no-deletion explanation must not be bypassed by the edit path.
          <p className="sm:col-span-2 lg:col-span-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
            {DISABLE_EXPLAINER}
          </p>
        )}
      </div>
      <Field label="Stopovers (JSON array — validated by the tracker CLI)">
        <textarea
          value={stopoversText}
          onChange={(e) => setStopoversText(e.target.value)}
          rows={4}
          placeholder='[{"carrier":"EY","point":"AUH","leg":"inbound","nights":{"min":1,"max":3}}]'
          spellCheck={false}
          className={`${inputClass} resize-y`}
        />
      </Field>
      {shownError && (
        <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 whitespace-pre-wrap">
          {shownError}
        </div>
      )}
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={submitting || saveDisabled}
          className="px-3 py-1.5 rounded bg-gray-900 text-sm font-medium text-white hover:bg-gray-700 disabled:opacity-50"
        >
          {submitting ? 'Saving…' : initial != null ? 'Save changes' : 'Create watch'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="px-3 py-1.5 rounded border border-gray-300 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
